/**
 * 1.8.2 — the one New at the top of Events (D-1.8.2-4). One sheet:
 *
 *   What do you want to do?   An event · An announcement · A survey — large buttons, one tap. Each is shown only to the
 *                             roles that may make that thing today (`newChoicesFor`, shared/new-kinds.ts); with one
 *                             choice the sheet starts on it.
 *   What kind of event?       In-house · Outing · Raffle · Calendar only (25 Oct 2026, D-1025-EVENTS), and the tick Needs
 *                             volunteers (and, quieter, A workshop — the 1.5.1 type that kept its own short form). The
 *                             kind maps onto the 1.7.9 kinds by `presetOfKind` and opens the 1.7.9 New event on that
 *                             kind's fields; its Back comes back here. A day cell's New starts here, with the day filled.
 *   An announcement           the 1.7.6 announcement draft, unchanged.
 *   A survey                  the 1.7.3 survey editor, unchanged.
 *
 * The three 1.7.x sheets are used as they are; this sheet only asks the first question or two.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { EventView } from '@shared/events';
import { NEW_EVENT_KINDS, NEW_RIGHTS, newChoicesFor, presetOfKind, type NewEventKind, type NewThing } from '@shared/new-kinds';
import type { Role } from '@shared/roles';
import { useAnnounce } from '@/components/Announcer';
import { Icon, type IconName } from '@/components/Icons';
import { StepHead } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { NewAnnouncementSheet } from '@/features/announcements/NewAnnouncementSheet';
import { SurveyEditor } from '@/features/surveys/SurveyEditor';
import { NewEventSheet } from './NewEventSheet';

type Step = 'what' | 'kind' | 'event' | 'announcement' | 'survey';
type Kind = NewEventKind | 'workshop';

const THING_ICON: Record<NewThing, IconName> = { event: 'events', announcement: 'mail', survey: 'file' };

export interface NewSheet182Props {
  role: Role | null;
  /** Who may make what (the API's gates); a test may narrow it. */
  rights?: Readonly<Record<NewThing, readonly Role[]>>;
  todayKey: string;
  returnFocusTo: RefObject<HTMLElement | null>;
  onClose: () => void;
  onCreated: (e: EventView) => void;
  /** A day cell's New: the sheet starts on An event with this day. */
  startDayKey?: string;
  start?: 'event';
}

export function NewSheet182({ role, rights = NEW_RIGHTS, todayKey, returnFocusTo, onClose, onCreated, startDayKey, start }: NewSheet182Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const choices = newChoicesFor(role, rights);
  const only = choices.length === 1 ? choices[0]! : null;
  const firstStep: Step = start === 'event' && choices.includes('event') ? 'kind' : only === 'event' ? 'kind' : only ?? 'what';
  const [step, setStep] = useState<Step>(firstStep);
  const [kind, setKind] = useState<Kind | null>(null);
  const [volunteers, setVolunteers] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const whatHead = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (step !== 'what') return;
    whatHead.current?.focus();
    announce(t('ev182.sheet.whatQ'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const pickThing = (thing: NewThing) => {
    setProblem(null);
    setStep(thing === 'event' ? 'kind' : thing);
  };
  const next = () => {
    if (!kind) {
      const p = t('ev182.sheet.needKind');
      setProblem(p);
      return announce(p, 'assertive');
    }
    setProblem(null);
    setStep('event');
  };

  if (step === 'event' && kind) {
    return (
      <NewEventSheet
        onClose={onClose}
        returnFocusTo={returnFocusTo}
        todayKey={todayKey}
        onCreated={onCreated}
        {...(startDayKey ? { startDayKey } : {})}
        preset={kind === 'workshop' ? 'workshop' : presetOfKind(kind, volunteers)}
        onBackToKind={() => setStep('kind')}
      />
    );
  }
  if (step === 'announcement') return <NewAnnouncementSheet existing={null} onClose={onClose} returnFocusTo={returnFocusTo} onSent={(d) => navigate(`/events/announcements/${encodeURIComponent(d.id)}`)} />;
  if (step === 'survey') return <SurveyEditor onClose={onClose} returnFocusTo={returnFocusTo} onSaved={(d) => navigate(`/events/surveys/${encodeURIComponent(d.survey.slug)}`)} />;

  const canGoBack = step === 'kind' && choices.length > 1;
  return (
    <SheetContent
      title={t('ev182.sheet.title')}
      returnFocusTo={returnFocusTo}
      data-testid="new-sheet"
      footer={
        step === 'kind' ? (
          <>
            {canGoBack ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setProblem(null);
                  setStep('what');
                }}
                data-testid="new-back"
              >
                {t('ev182.sheet.back')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={onClose} data-testid="new-cancel">
                {t('ev182.sheet.cancel')}
              </Button>
            )}
            <Button onClick={next} data-testid="new-next">
              {t('ev182.sheet.next')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose} data-testid="new-cancel">
            {t('ev182.sheet.cancel')}
          </Button>
        )
      }
    >
      {step === 'what' ? (
        <>
          <h3 ref={whatHead} tabIndex={-1} className="step-q" id="new-what-h">
            {t('ev182.sheet.whatQ')}
          </h3>
          <ul className="plain-list ev182-what" aria-labelledby="new-what-h">
            {choices.map((thing) => (
              <li key={thing}>
                <button type="button" className="ev182-whatbtn" onClick={() => pickThing(thing)} aria-describedby={`new-what-${thing}-help`} data-testid={`new-what-${thing}`}>
                  <Icon name={THING_ICON[thing]} aria-hidden="true" />
                  <span>
                    <b>{t(`ev182.sheet.${thing}`)}</b>
                    <small id={`new-what-${thing}-help`}>{t(`ev182.sheet.${thing}Help`)}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <StepHead n={1} of={3} question={t('ev182.sheet.kindQ')} id="new-kind-h" testId="new-kind-step" />
          <fieldset className="field ev182-kind" aria-labelledby="new-kind-h" data-testid="new-kind-choice">
            <div className="radio-cards">
              {NEW_EVENT_KINDS.map((k) => (
                <label key={k}>
                  <input type="radio" name="new-kind" value={k} checked={kind === k} onChange={() => setKind(k)} aria-describedby={`new-kind-${k}-help`} data-testid={`new-kind-${k}`} />
                  <span>
                    {t(`ev1025.kind.${k}`)}
                    <small id={`new-kind-${k}-help`}>{t(`ev1025.kind.${k}Help`)}</small>
                  </span>
                </label>
              ))}
            </div>
            <label className="ev179r-tick" htmlFor="new-kind-volunteers">
              <input id="new-kind-volunteers" type="checkbox" checked={volunteers && kind !== 'workshop'} disabled={kind === 'workshop'} onChange={(e) => setVolunteers(e.target.checked)} aria-describedby="new-kind-volunteers-help" data-testid="new-kind-volunteers" />
              <span>
                {t('ev182.sheet.volunteers')}
                <small id="new-kind-volunteers-help">{t('ev182.sheet.volunteersHelp')}</small>
              </span>
            </label>
            <div className="radio-cards ev182-workshop">
              <label>
                <input type="radio" name="new-kind" value="workshop" checked={kind === 'workshop'} onChange={() => setKind('workshop')} aria-describedby="new-kind-workshop-help" data-testid="new-kind-workshop" />
                <span>
                  {t('ev182.sheet.workshop')}
                  <small id="new-kind-workshop-help">{t('ev182.sheet.workshopHelp')}</small>
                </span>
              </label>
            </div>
          </fieldset>
        </>
      )}
      {problem ? (
        <p className="field-err" role="alert" data-testid="new-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
